import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { projectsApi, clientsApi, Client } from '../api';
import {
  Upload,
  FileJson,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileText,
  Layers,
  Home,
  Settings,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

interface ImportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ParsedConfig {
  site?: {
    shell?: {
      theme?: string;
      brand?: { title?: string };
    };
  };
  pages?: Array<{
    id: string;
    label?: string;
    enabled?: boolean;
    templateId?: string;
    route?: { pathPattern?: string };
    sections?: Array<{ sectionId: string }>;
  }>;
  routing?: object;
  dataSources?: Array<{ id: string }>;
}

interface ConfigSummary {
  siteName: string;
  theme: string;
  pagesCount: number;
  enabledPagesCount: number;
  sectionsCount: number;
  hasRouting: boolean;
  dataSourcesCount: number;
  pages: Array<{
    id: string;
    label: string;
    path: string;
    template: string;
    sectionsCount: number;
    enabled: boolean;
  }>;
}

export function ImportConfigModal({ isOpen, onClose }: ImportConfigModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState<'upload' | 'preview' | 'configure'>('upload');
  const [rawJson, setRawJson] = useState<string>('');
  const [parsedConfig, setParsedConfig] = useState<ParsedConfig | null>(null);
  const [configSummary, setConfigSummary] = useState<ConfigSummary | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showPagesDetail, setShowPagesDetail] = useState(false);

  // Carica lista clienti
  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'active'],
    queryFn: () => clientsApi.list({ limit: 100 }),
    enabled: isOpen,
    staleTime: 0, // Sempre fresh quando apriamo la modale
  });
  const clients = clientsData?.data?.data?.clients?.filter((c: Client) => c.active) || [];

  // Mutation per creare progetto
  const createMutation = useMutation({
    mutationFn: (data: { name: string; clientId: string; config: object }) =>
      projectsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      const projectId = response.data?.data?.project?.id;
      onClose();
      resetState();
      if (projectId) {
        navigate(`/projects/${projectId}`);
      }
    },
  });

  const resetState = () => {
    setStep('upload');
    setRawJson('');
    setParsedConfig(null);
    setConfigSummary(null);
    setParseError(null);
    setProjectName('');
    setSelectedClientId('');
    setShowPagesDetail(false);
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const analyzeConfig = (config: ParsedConfig): ConfigSummary => {
    const pages = config.pages || [];
    const enabledPages = pages.filter(p => p.enabled !== false);
    
    let totalSections = 0;
    const pagesSummary = pages.map(page => {
      const sectionsCount = page.sections?.length || 0;
      totalSections += sectionsCount;
      return {
        id: page.id,
        label: page.label || page.id,
        path: page.route?.pathPattern || '/',
        template: page.templateId || 'unknown',
        sectionsCount,
        enabled: page.enabled !== false,
      };
    });

    return {
      siteName: config.site?.shell?.brand?.title || 'Sito senza nome',
      theme: config.site?.shell?.theme || 'default',
      pagesCount: pages.length,
      enabledPagesCount: enabledPages.length,
      sectionsCount: totalSections,
      hasRouting: Boolean(config.routing),
      dataSourcesCount: config.dataSources?.length || 0,
      pages: pagesSummary,
    };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      processJsonContent(content);
    };
    reader.onerror = () => {
      setParseError('Errore nella lettura del file');
    };
    reader.readAsText(file);
  };

  const handlePaste = () => {
    if (rawJson.trim()) {
      processJsonContent(rawJson);
    }
  };

  const processJsonContent = (content: string) => {
    setParseError(null);
    
    try {
      const parsed = JSON.parse(content);
      
      // Validazione base
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('Il JSON deve essere un oggetto');
      }

      // Verifica struttura minima
      if (!parsed.pages && !parsed.site) {
        throw new Error('La configurazione deve contenere almeno "site" o "pages"');
      }

      setParsedConfig(parsed);
      setRawJson(content);
      
      const summary = analyzeConfig(parsed);
      setConfigSummary(summary);
      
      // Suggerisci nome progetto
      if (!projectName) {
        setProjectName(summary.siteName !== 'Sito senza nome' 
          ? summary.siteName 
          : 'Progetto importato');
      }
      
      setStep('preview');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'JSON non valido');
    }
  };

  const handleImport = () => {
    if (!parsedConfig || !projectName.trim() || !selectedClientId) return;

    createMutation.mutate({
      name: projectName.trim(),
      clientId: selectedClientId,
      config: parsedConfig,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Importa configurazione</h2>
              <p className="text-sm text-gray-500">
                {step === 'upload' && 'Carica un file customer-profile.json'}
                {step === 'preview' && 'Anteprima della configurazione'}
                {step === 'configure' && 'Completa i dettagli del progetto'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* File upload area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <FileJson className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-700 font-medium mb-1">
                  Clicca per selezionare un file
                </p>
                <p className="text-sm text-gray-500">
                  oppure trascina qui il file customer-profile.json
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-gray-500">oppure</span>
                </div>
              </div>

              {/* Paste JSON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incolla il JSON direttamente
                </label>
                <textarea
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  placeholder='{"site": {...}, "pages": [...]}'
                  className="input font-mono text-sm h-40"
                />
                <button
                  onClick={handlePaste}
                  disabled={!rawJson.trim()}
                  className="btn-primary mt-3 w-full"
                >
                  Analizza JSON
                </button>
              </div>

              {/* Error */}
              {parseError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Errore nel parsing</p>
                    <p className="text-sm text-red-600">{parseError}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && configSummary && (
            <div className="space-y-6">
              {/* Success message */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800 font-medium">
                  Configurazione valida! Anteprima pronta.
                </p>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Home className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-500 uppercase">Sito</span>
                  </div>
                  <p className="font-semibold text-gray-900 truncate">{configSummary.siteName}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Settings className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-500 uppercase">Tema</span>
                  </div>
                  <p className="font-semibold text-gray-900">{configSummary.theme}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-500 uppercase">Pagine</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {configSummary.enabledPagesCount}
                    <span className="text-gray-400 font-normal"> / {configSummary.pagesCount}</span>
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Layers className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-500 uppercase">Sezioni</span>
                  </div>
                  <p className="font-semibold text-gray-900">{configSummary.sectionsCount}</p>
                </div>
              </div>

              {/* Pages detail */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowPagesDetail(!showPagesDetail)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-900">
                    Dettaglio pagine ({configSummary.pages.length})
                  </span>
                  {showPagesDetail ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {showPagesDetail && (
                  <div className="max-h-48 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Pagina</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Path</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Template</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Sezioni</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Stato</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {configSummary.pages.map((page) => (
                          <tr key={page.id} className={clsx(!page.enabled && 'opacity-50')}>
                            <td className="px-4 py-2 font-medium text-gray-900">{page.label}</td>
                            <td className="px-4 py-2 font-mono text-gray-600">{page.path}</td>
                            <td className="px-4 py-2 text-gray-600">{page.template}</td>
                            <td className="px-4 py-2 text-center text-gray-600">{page.sectionsCount}</td>
                            <td className="px-4 py-2 text-center">
                              {page.enabled ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Project configuration */}
              <div className="border-t border-gray-200 pt-6 space-y-4">
                <h3 className="font-semibold text-gray-900">Dettagli progetto</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome progetto *
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Es. Museo Civico - Sito principale"
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente *
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="input"
                  >
                    <option value="">Seleziona un cliente...</option>
                    {clients.map((client: Client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} ({client.code})
                      </option>
                    ))}
                  </select>
                  {clients.length === 0 && (
                    <p className="mt-1 text-sm text-amber-600">
                      Nessun cliente disponibile. Crea prima un cliente.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <div>
            {step === 'preview' && (
              <button
                onClick={() => setStep('upload')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Torna indietro
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={handleClose} className="btn-secondary">
              Annulla
            </button>
            {step === 'preview' && (
              <button
                onClick={handleImport}
                disabled={!projectName.trim() || !selectedClientId || createMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importazione...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importa progetto
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Error from mutation */}
        {createMutation.isError && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200 flex items-center space-x-2 text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Errore nell'importazione: {createMutation.error?.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}
